import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Save, AlertTriangle, Info, Plus, X } from "lucide-react";

const DEFAULT_INSULATION_TYPES = [
  { value: "eps_grey", short: "EPS šedý" },
  { value: "eps_white", short: "EPS bílý" },
  { value: "mineral_wool", short: "Minerální vata – kontaktní" },
  { value: "pir", short: "PIR" },
  { value: "xps", short: "XPS (sokl)" },
  { value: "vent_facade", short: "Provětrávaná fasáda" },
];

const THICKNESSES = [5, 10, 15, 20, 25, 30];

interface PriceGrid {
  [key: string]: number | null;
}

interface CustomInsulation {
  value: string;
  short: string;
}

interface Props {
  supplierId: string;
}

export function SupplierPriceCatalog({ supplierId }: Props) {
  const [prices, setPrices] = useState<PriceGrid>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [enabledTypes, setEnabledTypes] = useState<Record<string, boolean>>({
    eps_grey: true,
    eps_white: true,
    mineral_wool: true,
    pir: true,
    xps: false,
    vent_facade: false,
  });
  const [customTypes, setCustomTypes] = useState<CustomInsulation[]>([]);
  const [newCustomName, setNewCustomName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const allTypes = [
    ...DEFAULT_INSULATION_TYPES,
    ...customTypes.map((c) => ({ value: c.value, short: c.short })),
  ];

  const activeTypes = allTypes.filter((t) => enabledTypes[t.value]);

  useEffect(() => {
    loadItems();
  }, [supplierId]);

  const loadItems = async () => {
    const { data, error } = await supabase
      .from("supplier_price_items")
      .select("*")
      .eq("supplier_id", supplierId)
      .eq("is_active", true);

    if (!error && data && data.length > 0) {
      const grid: PriceGrid = {};
      const foundTypes = new Set<string>();
      const customSet = new Set<string>();
      const loadedNotes: Record<string, string> = {};

      data.forEach((d) => {
        grid[`${d.insulation_type}_${d.thickness_cm}`] = d.price_per_m2;
        foundTypes.add(d.insulation_type);
        if (d.notes && !loadedNotes[d.insulation_type]) {
          loadedNotes[d.insulation_type] = d.notes;
        }
        if (!DEFAULT_INSULATION_TYPES.some((t) => t.value === d.insulation_type)) {
          customSet.add(d.insulation_type);
        }
      });

      setPrices(grid);
      setNotes(loadedNotes);
      setCustomTypes(Array.from(customSet).map((v) => ({ value: v, short: v })));

      // Enable only types that have data, disable rest
      const enabled: Record<string, boolean> = {};
      DEFAULT_INSULATION_TYPES.forEach((t) => {
        enabled[t.value] = foundTypes.has(t.value);
      });
      customSet.forEach((v) => {
        enabled[v] = true;
      });
      setEnabledTypes(enabled);
    }
    setLoading(false);
  };

  const getKey = (type: string, thickness: number) => `${type}_${thickness}`;

  const updatePrice = (type: string, thickness: number, value: string) => {
    const key = getKey(type, thickness);
    const num = parseFloat(value);
    setPrices((prev) => ({
      ...prev,
      [key]: value === "" ? null : isNaN(num) ? null : num,
    }));
  };

  const toggleType = (value: string) => {
    setEnabledTypes((prev) => ({ ...prev, [value]: !prev[value] }));
  };

  const addCustomType = () => {
    const name = newCustomName.trim();
    if (!name) return;
    const value = `custom_${name.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "")}`;
    if (allTypes.some((t) => t.value === value)) {
      toast.error("Tento izolant už existuje");
      return;
    }
    setCustomTypes((prev) => [...prev, { value, short: name }]);
    setEnabledTypes((prev) => ({ ...prev, [value]: true }));
    setNewCustomName("");
  };

  const removeCustomType = (value: string) => {
    setCustomTypes((prev) => prev.filter((c) => c.value !== value));
    setEnabledTypes((prev) => {
      const next = { ...prev };
      delete next[value];
      return next;
    });
    setPrices((prev) => {
      const next = { ...prev };
      THICKNESSES.forEach((t) => delete next[getKey(value, t)]);
      return next;
    });
  };

  const filledCount = Object.entries(prices).filter(
    ([key, v]) => v && v > 0 && activeTypes.some((t) => key.startsWith(t.value + "_"))
  ).length;
  const totalCells = activeTypes.length * THICKNESSES.length;

  const saveAll = async () => {
    if (filledCount === 0) {
      toast.error("Vyplňte alespoň jednu cenu");
      return;
    }

    setSaving(true);

    const { error: deleteError } = await supabase
      .from("supplier_price_items")
      .delete()
      .eq("supplier_id", supplierId);

    if (deleteError) {
      console.error(deleteError);
      toast.error("Chyba při ukládání");
      setSaving(false);
      return;
    }

    const rows: any[] = [];
    for (const type of activeTypes) {
      for (const thickness of THICKNESSES) {
        const price = prices[getKey(type.value, thickness)];
        if (price && price > 0) {
          rows.push({
            supplier_id: supplierId,
            insulation_type: type.value,
            thickness_cm: thickness,
            plaster_type: "standard",
            price_per_m2: price,
            includes_material: true,
            includes_labor: true,
            includes_scaffolding: true,
            is_active: true,
            notes: notes[type.value] || null,
          });
        }
      }
    }

    if (rows.length > 0) {
      const { error } = await supabase.from("supplier_price_items").insert(rows);
      if (error) {
        console.error(error);
        toast.error("Chyba při ukládání");
        setSaving(false);
        return;
      }
    }

    toast.success("Ceník uložen ✅");
    await loadItems();
    setSaving(false);
  };

  if (loading) {
    return <div className="text-muted-foreground text-sm py-4">Načítám ceník...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Info box */}
      <div className="bg-accent/50 border border-border rounded-xl p-4 text-sm text-foreground space-y-2">
        <p className="font-medium">📋 Ceník fasád a zateplení</p>
        <ul className="text-muted-foreground text-xs space-y-1 list-disc list-inside">
          <li>Vyplňte cenu za m² <strong>bez DPH</strong> (kompletní – materiál + práce + lešení)</li>
          <li>AI automaticky dopočítá mezilehlé tloušťky (např. 12, 18 cm)</li>
          <li>Ceny se anonymně použijí v AI kalkulačce pro zákazníky</li>
          <li>K materiálu můžete přidat <strong>poznámku</strong> – AI ji využije pro přesnější odpovědi</li>
          <li><strong>Bez ceníku se vaše firma nezobrazuje v nabídkách</strong></li>
        </ul>
      </div>

      {filledCount === 0 && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
          <span className="text-xs text-destructive font-medium">Nemáte vyplněný ceník – vaše firma se nezobrazuje zákazníkům</span>
        </div>
      )}

      {/* Price grid table */}
      {activeTypes.length > 0 ? (
        <div className="overflow-x-auto border border-border rounded-xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-3 py-2.5 font-medium text-muted-foreground text-xs w-8" />
                <th className="text-left px-1 py-2.5 font-medium text-muted-foreground text-xs">
                  Izolant
                </th>
                {THICKNESSES.map((t) => (
                  <th key={t} className="text-center px-2 py-2.5 font-medium text-muted-foreground text-xs min-w-[80px]">
                    {t} cm
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allTypes.map((type, typeIdx) => {
                const enabled = !!enabledTypes[type.value];
                const isCustom = !DEFAULT_INSULATION_TYPES.some((t) => t.value === type.value);

                return (
                  <>
                    <tr
                      key={type.value}
                      className={`${typeIdx < allTypes.length - 1 ? "border-b border-border" : ""} ${!enabled ? "opacity-40" : ""}`}
                    >
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={() => toggleType(type.value)}
                          className="rounded border-border"
                        />
                      </td>
                      <td className="px-1 py-2.5 font-medium text-foreground text-xs whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          {type.short}
                          {isCustom && (
                            <button
                              onClick={() => removeCustomType(type.value)}
                              className="p-0.5 text-destructive/60 hover:text-destructive"
                              title="Odebrat"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                      {THICKNESSES.map((thickness) => {
                        const key = getKey(type.value, thickness);
                        const val = prices[key];
                        return (
                          <td key={thickness} className="px-1.5 py-1.5">
                            <input
                              type="number"
                              min="0"
                              disabled={!enabled}
                              value={val ?? ""}
                              onChange={(e) => updatePrice(type.value, thickness, e.target.value)}
                              placeholder="—"
                              className="w-full text-sm bg-background border border-border rounded-lg px-2 py-1.5 text-foreground text-center focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                        );
                      })}
                    </tr>
                    {enabled && (
                      <tr key={`${type.value}-note`} className={`${typeIdx < allTypes.length - 1 ? "border-b border-border" : ""}`}>
                        <td />
                        <td colSpan={THICKNESSES.length + 1} className="px-1 pb-2">
                          <input
                            type="text"
                            value={notes[type.value] || ""}
                            onChange={(e) => setNotes((prev) => ({ ...prev, [type.value]: e.target.value }))}
                            placeholder="Poznámka pro AI (např. včetně roštu a difuzní fólie)…"
                            className="w-full text-[11px] bg-muted/30 border border-border/50 rounded-lg px-2 py-1 text-muted-foreground focus:text-foreground focus:border-primary/30 transition-colors placeholder:text-muted-foreground/50"
                          />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
              {/* Add custom row */}
              <tr className="border-t border-border bg-muted/20">
                <td className="px-3 py-2" colSpan={THICKNESSES.length + 2}>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newCustomName}
                      onChange={(e) => setNewCustomName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addCustomType()}
                      placeholder="Vlastní izolant…"
                      className="text-xs bg-background border border-border rounded-lg px-2 py-1.5 text-foreground w-44"
                    />
                    <button
                      onClick={addCustomType}
                      disabled={!newCustomName.trim()}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 border border-border rounded-lg hover:bg-accent transition-colors text-foreground disabled:opacity-40"
                    >
                      <Plus className="w-3 h-3" />
                      Přidat
                    </button>
                  </div>
                </td>
              </tr>
              {/* "Ostatní" info row */}
              <tr className="border-t border-border bg-muted/30">
                <td className="px-3 py-2.5 font-medium text-muted-foreground text-xs" colSpan={THICKNESSES.length + 2}>
                  <div className="flex items-center gap-2">
                    <Info className="w-3.5 h-3.5" />
                    <span>Atypické tloušťky / materiály mimo ceník → <strong className="text-foreground">na poptávku</strong></span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-sm text-muted-foreground border border-dashed border-border rounded-xl">
          Zaškrtněte alespoň jeden izolant výše
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {activeTypes.length > 0 ? `Vyplněno ${filledCount} z ${totalCells} variant · Kč/m² bez DPH` : ""}
        </span>
        <button
          onClick={saveAll}
          disabled={saving || activeTypes.length === 0}
          className="flex items-center gap-1.5 px-5 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm font-medium"
        >
          <Save className="w-4 h-4" />
          {saving ? "Ukládám..." : "Uložit ceník"}
        </button>
      </div>
    </div>
  );
}
